// ═══════════════════════════════════════════════════════════════
//  App.jsx — Routeur racine de l'EPJ App Globale
//  v1.12.0 :
//   • Ajout de la route "change-password" (libre depuis le menu)
//   • Forcing mustResetPassword : si le user a ce flag à true,
//     l'app le redirige automatiquement vers ChangePasswordPage
//     en mode "forced". Aucun autre écran n'est accessible.
// ═══════════════════════════════════════════════════════════════
import { useState, useEffect, useRef, lazy, Suspense } from "react";

import { EPJ } from "./core/theme";
import { DataProvider, useData } from "./core/DataContext";
import { AuthProvider, useAuth } from "./core/AuthContext";
import { ToastProvider } from "./core/components/Toast";
import { Layout } from "./core/Layout";
import { Spinner, FullPageSpinner } from "./core/components/Spinner";
import { can } from "./core/permissions";
import { ancreDeRoute, routeDepuisAncre, idDepuisAncre } from "./core/routes";
import { BandeauHorsLigne, useEnLigne } from "./core/components/BandeauHorsLigne";

// Chemin de boot (login → home) : import statique, doit peindre immédiatement.
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
// Watchers de rappel : toujours montés dans le shell authentifié, légers.
import { OutillageRappelWatcher } from "./modules/parc-machines/OutillageRappelWatcher";
import { ReservesRappelWatcher } from "./modules/reserves/ReservesRappelWatcher";

// ─── Code-splitting (PERF-1) ──────────────────────────────────────
// Tout ce qui est hors du chemin de boot est chargé à la demande via
// React.lazy : les modules (commandes/avancement/parc/réserves), l'admin
// et les pages secondaires sortent du bundle initial (`index`).
// helper : nos modules/pages sont des exports NOMMÉS → on les remappe en
// `default` pour React.lazy.
const named = (loader, key) => lazy(() => loader().then(m => ({ default: m[key] })));

const CommandesModule    = named(() => import("./modules/commandes/CommandesModule"), "CommandesModule");
const AvancementModule   = named(() => import("./modules/avancement/AvancementModule"), "AvancementModule");
const GestionChantierModule = named(() => import("./modules/gestion-chantier/GestionChantierModule"), "GestionChantierModule");
const PlanningPage       = named(() => import("./modules/planning/PlanningPage"), "PlanningPage");
const RHModule           = named(() => import("./modules/rh/RHModule"), "RHModule");
const TachesPage         = named(() => import("./modules/taches/TachesPage"), "TachesPage");
const ChiffragePage = named(() => import("./modules/chiffrage/ChiffragePage"), "ChiffragePage");
const CarnetMaterielPage = named(() => import("./modules/carnet-materiel/CarnetMaterielPage"), "CarnetMaterielPage");
const InterventionsPage  = named(() => import("./modules/interventions/InterventionsPage"), "InterventionsPage");
const ParcMachinesModule = named(() => import("./modules/parc-machines/ParcMachinesModule"), "ParcMachinesModule");
const ReservesModule     = named(() => import("./modules/reserves/ReservesModule"), "ReservesModule");
const AdminPage          = named(() => import("./pages/admin/AdminPage"), "AdminPage");
// Cockpit Direction — seul écran de la route `dashboard:direction` depuis le
// lot 2C. L'ancien DashboardDirection et sa route de repli
// `dashboard:direction-legacy` ont été supprimés après validation en prod.
const CockpitConducteur = named(() => import("./modules/conducteur/CockpitConducteur"), "CockpitConducteur");
const CockpitDirection   = named(() => import("./modules/cockpit/CockpitDirection"), "CockpitDirection");
const CollectionDashboards = named(() => import("./pages/CollectionDashboards"), "CollectionDashboards");
const ChangePasswordPage = named(() => import("./pages/ChangePasswordPage"), "ChangePasswordPage");

const ROUTE_STORAGE_KEY = "epj_last_route";

// Fallback de chargement d'un écran lazy, dans le cadre du Layout.
function RouteFallback() {
  return (
    <div style={{ padding: "48px 8px", display: "flex", justifyContent: "center" }}>
      <Spinner label="Chargement…" size={26} />
    </div>
  );
}

export default function App() {
  return (
    <DataProvider>
      <AuthProvider>
        <ToastProvider>
          {/* T8 — monté ICI, pas dans Router() : Router a quatre branches de
              retour (login, spinner, mot de passe forcé, Layout) et le
              bandeau « pas de connexion » doit être visible sur les quatre. */}
          <BandeauHorsLigne/>
          <Router />
        </ToastProvider>
      </AuthProvider>
    </DataProvider>
  );
}

function Router() {
  const { user, mustResetPassword } = useAuth();
  const { allLoaded, rolesConfig } = useData();

  // ─── T8 — l'URL désigne un écran ────────────────────────────
  // Route initiale : ancre d'URL > dernière route mémorisée > "home".
  // SANS ancre, le comportement est celui d'avant T8, à l'identique.
  const [route, setRoute] = useState(() => {
    const depuisAncre = routeDepuisAncre(window.location.hash);
    if (depuisAncre) return depuisAncre;
    try { return localStorage.getItem(ROUTE_STORAGE_KEY) || "home"; }
    catch { return "home"; }
  });

  // 🔴 Ancre présente au TOUT PREMIER RENDU, avant que l'effet de
  // synchronisation ne réécrive le hash. Capturée ici (initialiseur de
  // useRef, évalué au rendu) et SURTOUT PAS dans un effet, qui s'exécuterait
  // trop tard. Sert UNE SEULE FOIS, à la première connexion, à neutraliser
  // la remise à "home" — sans quoi un lien direct ne servirait à rien.
  const ancreInitiale = useRef(routeDepuisAncre(window.location.hash));

  // État réseau — hook déclaré ICI, avant les early returns (règles des hooks).
  const enLigne = useEnLigne();

  // Cible optionnelle transmise par onOpenModule (ex. ouvrir directement une
  // commande depuis la bannière "messages non lus"). Non persistée (état
  // volatile) → pas de ré-ouverture au reload. Consommée one-shot par le module.
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const openModule = (mod, opts) => {
    setPendingOrderId(opts?.orderId || null);
    setRoute(`module:${mod}`);
  };

  useEffect(() => {
    try { localStorage.setItem(ROUTE_STORAGE_KEY, route); } catch {}
  }, [route]);

  // ─── route -> ancre ─────────────────────────────────────────
  // replaceState et NON pushState. TOUS les boutons « retour » de l'app
  // appellent setRoute("home") : en push, chaque aller-retour empilerait deux
  // entrées et le bouton retour du navigateur remonterait indéfiniment cette
  // pile sans jamais sortir de l'app. En replace, l'URL décrit toujours
  // l'écran courant (partageable, rechargeable) et le bouton retour se
  // comporte EXACTEMENT comme avant T8.
  useEffect(() => {
    const ancre = ancreDeRoute(route);
    const cible = ancre ? `#${ancre}` : "";
    if (window.location.hash === cible) return;
    // Ancre désignant une FICHE de la route courante (« #/interventions/<id> ») :
    // on la garde telle quelle, sinon l'id serait effacé avant que l'écran
    // ne le lise. C'est l'écran qui la réécrit en naviguant.
    if (idDepuisAncre(window.location.hash) && routeDepuisAncre(window.location.hash) === route) return;
    try {
      window.history.replaceState(null, "",
        window.location.pathname + window.location.search + cible);
    } catch {}
  }, [route]);

  // ─── ancre -> route ─────────────────────────────────────────
  // Retour arrière du navigateur, ou URL éditée à la main. Une ancre inconnue
  // ou vide ne déplace RIEN : on ne force jamais "home" depuis ici, ça
  // bouclerait avec l'effet ci-dessus.
  useEffect(() => {
    const surHashChange = () => {
      const r = routeDepuisAncre(window.location.hash);
      if (r) setRoute(r);
    };
    window.addEventListener("hashchange", surHashChange);
    return () => window.removeEventListener("hashchange", surHashChange);
  }, []);

  // Utilisateur dont la route initiale a déjà été posée. 🔴 NE PAS remplacer
  // par un `ancreInitiale.current = null` : <StrictMode> monte, démonte et
  // remonte les effets en dev, donc cet effet tourne DEUX fois au premier
  // montage. Vider le ref au 1er passage faisait retomber le 2e sur "home"
  // et annulait tout lien direct. Ici, le 2e passage sort immédiatement.
  const userIdTraite = useRef(null);

  const userId = user?._id || null;
  useEffect(() => {
    if (!userId) return;
    if (userIdTraite.current === userId) return;   // 2e passage StrictMode
    const premierUtilisateur = userIdTraite.current === null;
    userIdTraite.current = userId;
    // L'ancre d'URL ne vaut QUE pour le premier utilisateur résolu de ce
    // chargement de page : après un changement de compte, on retombe sur
    // l'accueil, comme avant T8.
    setRoute((premierUtilisateur && ancreInitiale.current) || "home");
  }, [userId]);

  // v2.0.0 — fix runtime : tester !user AVANT !allLoaded.
  // Le DataContext n'ouvre ses listeners qu'après authReady (= user connecté +
  // JWT refreshé). Sans user, aucun loaded.xxx ne passe à true et allLoaded
  // reste false pour toujours → l'app resterait bloquée sur le spinner alors
  // qu'elle devrait afficher la page de login.
  if (!user) return <LoginPage/>;
  // T8 — spinner sensible au réseau. Sans réseau et sans persistance
  // Firestore, allLoaded ne passe jamais à true : l'app resterait bloquée sur
  // « Chargement… » sans rien dire.
  if (!allLoaded) return (
    <FullPageSpinner label={enLigne
      ? "Chargement de l'application…"
      : "Pas de connexion — en attente du réseau…"}/>
  );

  // ─── v1.12.0 — Forcing mustResetPassword ─────────────────────────
  // Si le compte a le flag à true (compte créé avec mdp temporaire),
  // on bloque TOUT accès à l'app tant que le mdp n'est pas changé.
  if (mustResetPassword) {
    return (
      <Suspense fallback={<FullPageSpinner label="Chargement…"/>}>
        <ChangePasswordPage
          mode="forced"
          onDone={() => {
            // Après reset, le flag passe à false côté Firestore, la sync
            // automatique va rafraîchir user.mustResetPassword et on
            // sortira de ce return naturellement.
            setRoute("home");
          }}
        />
      </Suspense>
    );
  }

  const openBestDashboard = () => {
    if (can(user, "_dashboards", "direction", rolesConfig)) setRoute("dashboard:direction");
    else if (can(user, "_dashboards", "conducteur", rolesConfig)) setRoute("dashboard:conducteur");
    else if (can(user, "_dashboards", "public", rolesConfig)) setRoute("dashboard:public");
  };

  const currentModule = (() => {
    if (route === "home")                  return "Accueil";
    if (route === "module:commandes")      return "Commandes";
    if (route === "module:avancement")     return "Avancement chantier";
    if (route === "module:gestionChantier") return "Gestion de chantier";
    if (route === "module:planning")       return "Planning";
    if (route === "module:rh")             return "Ressources humaines";
    if (route === "module:taches")         return "Tâches";
    if (route === "module:chiffrage")     return "Chiffrage";
    if (route === "module:carnetMateriel") return "Carnet de matériel";
    if (route === "module:interventions")  return "Interventions";
    if (route === "module:parc-machines")  return "Parc machines";
    if (route === "module:reserves")       return "Réserves & quitus";
    if (route === "dashboard:direction")   return "Cockpit Direction";
    if (route === "dashboards")            return "Collection Dashboards";
    if (route === "dashboard:conducteur")  return "Dashboard Conducteur";
    if (route === "dashboard:public")      return "Dashboard Public";
    if (route === "admin")                 return "Administration";
    if (route === "change-password")       return "Mot de passe";
    return null;
  })();

  // Lot 0 desktop : la largeur du cadre est désormais pilotée par le shell
  // (Layout, seuil 760 px), plus par route. Plus de calcul fullWidth ici.
  const headerOnBack = route === "home" ? null : () => setRoute("home");

  // ─── v1.12.0 — Changement de mot de passe libre (depuis menu) ───
  // Affiché en standalone (sans Layout) pour avoir un écran propre,
  // comme l'écran de login.
  if (route === "change-password") {
    return (
      <Suspense fallback={<FullPageSpinner label="Chargement…"/>}>
        <ChangePasswordPage
          mode="free"
          onDone={() => setRoute("home")}
          onCancel={() => setRoute("home")}
        />
      </Suspense>
    );
  }

  return (
    <Layout
      appearance={["dashboard:direction", "dashboard:conducteur"].includes(route) ? "cockpit" : undefined}
      currentModule={currentModule}
      onHome={() => setRoute("home")}
      onBack={headerOnBack}
      onOpenAdmin={() => setRoute("admin")}
      onChangePassword={() => setRoute("change-password")}
    >
      <OutillageRappelWatcher/>
      <ReservesRappelWatcher/>

      <Suspense fallback={<RouteFallback/>}>
      {route === "home" && (
        <HomePage
          onOpenModule={openModule}
          onOpenDashboard={openBestDashboard}
          onOpenCollectionDashboards={() => setRoute("dashboards")}
        />
      )}

      {route === "dashboards" && (
        <CollectionDashboards onBack={() => setRoute("home")} />
      )}

      {route === "module:commandes" && (
        <CommandesModule onExitModule={() => setRoute("home")} initialOrderId={pendingOrderId}/>
      )}

      {route === "module:avancement" && (
        <AvancementModule onExitModule={() => setRoute("home")}/>
      )}

      {route === "module:gestionChantier" && (
        <GestionChantierModule onExitModule={() => setRoute("home")}/>
      )}

      {route === "module:planning" && (
        <PlanningPage onExitModule={() => setRoute("home")}/>
      )}

      {route === "module:rh" && (
        <RHModule onExitModule={() => setRoute("home")} onNavigate={(r) => setRoute(r)}/>
      )}

      {route === "module:taches" && (
        <TachesPage onExitModule={() => setRoute("home")}/>
      )}

      {route === "module:chiffrage" && (
        <ChiffragePage/>
      )}

      {route === "module:carnetMateriel" && (
        <CarnetMaterielPage onExitModule={() => setRoute("home")}/>
      )}

      {route === "module:interventions" && (
        <InterventionsPage/>
      )}

      {route === "module:parc-machines" && (
        <ParcMachinesModule onExitModule={() => setRoute("home")}/>
      )}

      {route === "module:reserves" && (
        <ReservesModule onExitModule={() => setRoute("home")}/>
      )}

      {route === "dashboard:direction" && (
        <CockpitDirection onBack={() => setRoute("home")}/>
      )}

      {route === "dashboard:conducteur" && <CockpitConducteur onBack={() => setRoute("home")} onNavigate={setRoute} />}

      {route === "dashboard:public" && (
        // TODO v2.0.1 : filtrer chantiers c.statut === "Terminé" (toujours masqués, pas de toggle) quand le vrai dashboard public sera développé
        <PlaceholderScreen title={currentModule} onBack={() => setRoute("home")}/>
      )}

      {route === "admin" && (
        <AdminPage onExit={() => setRoute("home")}/>
      )}
      </Suspense>
    </Layout>
  );
}

function PlaceholderScreen({ title, onBack }) {
  return (
    <div style={{ padding: "32px 8px", textAlign: "center" }}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>🛠️</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: EPJ.gray900, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: EPJ.gray500, marginBottom: 20 }}>
        À construire dans une prochaine livraison du Socle.
      </div>
      <button
        onClick={onBack}
        style={{
          background: EPJ.gray900, color: EPJ.white, border: "none",
          borderRadius: 10, padding: "10px 18px",
          fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}
      >
        ← Retour à l'accueil
      </button>
    </div>
  );
}
